import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useFieldArray, Controller, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Select from '@/components/Common/Select';
import Input from '@/components/Common/Input';
import TimePicker from 'components/Common/TimePicker';

interface CovetrusFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const daysOfWeek = [
  { label: 'Monday', value: 'Monday' },
  { label: 'Tuesday', value: 'Tuesday' },
  { label: 'Wednesday', value: 'Wednesday' },
  { label: 'Thursday', value: 'Thursday' },
  { label: 'Friday', value: 'Friday' },
  { label: 'Saturday', value: 'Saturday' },
  { label: 'Sunday', value: 'Sunday' }
];

const normalizeSelectOptions = (options: unknown) => (Array.isArray(options) ? options : []);

const defaultWorkingDay = () => ({
  day: 'Monday',
  start_time: '09:00',
  end_time: '17:00',
  appointment_types: [] as string[]
});

const defaultProvider = () => ({
  provider: '',
  accept_new_patient: false,
  working_days: [defaultWorkingDay()]
});

const defaultAppointmentType = () => ({
  appointment_type: '',
  duration: ''
});

const CovetrusForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: CovetrusFormProps) => {
  const providersField = configFields?.find((field: any) => field.key === 'providers');
  const appointmentTypesField = configFields?.find((field: any) => field.key === 'appointment_types');
  const otherFields = configFields?.filter(
    (field: any) => field.key !== 'providers' && field.key !== 'appointment_types'
  );

  const {
    fields: providers,
    append: appendProvider,
    remove: removeProvider
  } = useFieldArray({ control, name: `${namePrefix}.providers` });

  const {
    fields: appointmentTypes,
    append: appendApptType,
    remove: removeApptType
  } = useFieldArray({ control, name: `${namePrefix}.appointment_types` });

  useEffect(() => {
    const existing = getValues(`${namePrefix}.providers`);
    if (!existing || existing.length === 0) {
      appendProvider(defaultProvider());
    }
  }, [appendProvider, getValues, namePrefix]);

  useEffect(() => {
    const existing = getValues(`${namePrefix}.appointment_types`);
    if (!existing || existing.length === 0) {
      appendApptType(defaultAppointmentType());
    }
  }, [appendApptType, getValues, namePrefix]);

  const providerField = providersField?.group?.find((f: any) => f.key === 'provider');
  const providerOptions = normalizeSelectOptions(providerField?.options);

  const apptTypeSelectField = appointmentTypesField?.group?.find((f: any) => f.key === 'appointment_type');
  const allAppointmentTypeOptions = normalizeSelectOptions(apptTypeSelectField?.options);

  // Working-hours dropdown only shows appointment types the user added above.
  const watchedApptTypes = useWatch({ control, name: `${namePrefix}.appointment_types` });
  const selectedAppointmentTypeOptions = (watchedApptTypes || []).flatMap((t: any) => {
    const value = t?.appointment_type?.toString?.() || t?.appointment_type;
    if (!value) return [];

    const option = allAppointmentTypeOptions.find((o: any) => o.value?.toString() === value.toString());

    return [
      {
        value,
        label: option?.label || value
      }
    ];
  });

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      {appointmentTypesField && (
        <Box mt={3}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
            <Typography fontSize={16} fontWeight={600}>
              Appointment Types
            </Typography>
            <Button
              variant='contained'
              size='small'
              disabled={disabled}
              onClick={() => appendApptType(defaultAppointmentType())}
            >
              + Add Appointment Type
            </Button>
          </Stack>

          {appointmentTypes.map((appt: any, aIdx: number) => {
            const apptPrefix = `${namePrefix}.appointment_types.${aIdx}`;
            const currentValue = watchedApptTypes?.[aIdx]?.appointment_type?.toString?.() || '';
            const usedElsewhere = new Set(
              (watchedApptTypes || [])
                .map((t: any, i: number) =>
                  i === aIdx ? null : t?.appointment_type?.toString?.() || t?.appointment_type
                )
                .filter((v: any) => v !== null && v !== undefined && v !== '')
                .map((v: any) => String(v))
            );
            const availableOptions = allAppointmentTypeOptions.filter(
              (opt: any) => !usedElsewhere.has(String(opt.value)) || String(opt.value) === currentValue
            );

            return (
              <Grid container spacing={2} alignItems='center' mb={2} key={appt.id}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Select
                    label='Appointment Type'
                    name={`${apptPrefix}.appointment_type`}
                    control={control}
                    disabled={disabled}
                    options={availableOptions}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Input
                    name={`${apptPrefix}.duration`}
                    control={control}
                    label='Duration (min)'
                    placeholder='e.g. 30'
                    type='number'
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                {appointmentTypes.length > 1 && !disabled && (
                  <Grid size='auto'>
                    <IconButton color='error' onClick={() => removeApptType(aIdx)}>
                      <RiDeleteBin6Line />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            );
          })}
        </Box>
      )}

      {providersField && (
        <Box mt={3}>
          <Typography fontSize={16} fontWeight={600} mb={2}>
            Provider Configuration
          </Typography>

          {providers.map((provider: any, index: number) => (
            <ProviderItem
              key={provider.id}
              index={index}
              control={control}
              remove={removeProvider}
              disabled={disabled}
              getValues={getValues}
              setValue={setValue}
              namePrefix={namePrefix}
              totalProviders={providers.length}
              providerOptions={providerOptions}
              appointmentTypeOptions={selectedAppointmentTypeOptions}
            />
          ))}

          <Box display='flex' justifyContent='center'>
            <Button variant='contained' disabled={disabled} onClick={() => appendProvider(defaultProvider())}>
              + Add Another Provider
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default CovetrusForm;

interface ProviderItemProps {
  index: number;
  control: any;
  remove: (index: number) => void;
  disabled?: boolean;
  getValues: any;
  setValue: any;
  namePrefix: string;
  totalProviders: number;
  providerOptions: any[];
  appointmentTypeOptions: any[];
}

const ProviderItem = ({
  index,
  control,
  remove,
  disabled,
  getValues,
  setValue,
  namePrefix,
  totalProviders,
  providerOptions,
  appointmentTypeOptions
}: ProviderItemProps) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;

  const {
    fields: workingDays,
    append: appendWorkingDay,
    remove: removeWorkingDay
  } = useFieldArray({ control, name: `${providerPrefix}.working_days` });

  useEffect(() => {
    const existing = getValues(`${providerPrefix}.working_days`);
    if (!existing || existing.length === 0) {
      setValue(`${providerPrefix}.working_days`, [defaultWorkingDay()]);
    }
  }, [getValues, providerPrefix, setValue]);

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>
        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={2} alignItems='center'>
        <Grid size={{ xs: 12, md: 7 }}>
          <Select
            label='Provider'
            name={`${providerPrefix}.provider`}
            control={control}
            disabled={disabled}
            options={providerOptions}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Controller
            name={`${providerPrefix}.accept_new_patient`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label='Accepts new patients'
              />
            )}
          />
        </Grid>
      </Grid>

      <Stack direction='row' justifyContent='space-between' alignItems='center' mt={3} mb={2}>
        <Typography fontWeight={500}>Working Hours</Typography>
        <Button
          variant='outlined'
          size='small'
          disabled={disabled}
          onClick={() => appendWorkingDay(defaultWorkingDay())}
        >
          + Add Hours
        </Button>
      </Stack>

      {workingDays.map((slot: any, slotIdx: number) => {
        const slotPrefix = `${providerPrefix}.working_days.${slotIdx}`;
        return (
          <Grid container spacing={2} alignItems='center' mb={2} key={slot.id}>
            <Grid size={{ xs: 12, md: 2.5 }}>
              <Select
                label='Day'
                name={`${slotPrefix}.day`}
                control={control}
                disabled={disabled}
                options={daysOfWeek}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TimePicker name={`${slotPrefix}.start_time`} control={control} disabled={disabled} />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TimePicker name={`${slotPrefix}.end_time`} control={control} disabled={disabled} />
            </Grid>
            <Grid size={{ xs: 12, md: 4.5 }}>
              <Select
                label='Appointment types'
                name={`${slotPrefix}.appointment_types`}
                control={control}
                disabled={disabled}
                options={appointmentTypeOptions}
                multiple
                fullWidth
              />
            </Grid>
            {workingDays.length > 1 && !disabled && (
              <Grid size='auto'>
                <IconButton color='error' onClick={() => removeWorkingDay(slotIdx)}>
                  <RiDeleteBin6Line />
                </IconButton>
              </Grid>
            )}
          </Grid>
        );
      })}
    </Box>
  );
};
